import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Calendar, Tag, User, Clock, FileText, Target } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ActivityMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityMetadata: any;
}

export const ActivityMetadataModal: React.FC<ActivityMetadataModalProps> = ({
  isOpen,
  onClose,
  activityMetadata,
}) => {
  const { t } = useTranslation();

  if (!activityMetadata) return null;

  const activityName = activityMetadata?.name || "Activity";
  const description = activityMetadata?.description;
  const objectives = activityMetadata?.objectives || [];
  const author = activityMetadata?.fiUser;
  const vendor = activityMetadata?.vendor;
  const createdDate = activityMetadata?.createdOn;
  const lastUpdated = activityMetadata?.lastUpdated;
  const tags = activityMetadata?.tags || [];

  const authorName =
    author?.displayName ||
    `${author?.firstName || ""} ${author?.lastName || ""}`.trim() ||
    author?.email;

  const vendorName = vendor?.displayName || vendor?.name;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t("activity_details") || "Activity Details"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Activity Name */}
          <div>
            <h3 className="mb-2 text-3xl font-bold text-gray-900">
              {activityName}
            </h3>
          </div>

          {/* Description */}
          {description && (
            <div className="p-4 border border-primary/10 rounded-lg bg-primary/5">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">
                    {t("description") || "Description"}
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                    {description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Learning Objectives */}
          {objectives.length > 0 && (
            <div className="p-4 border border-purple-100 rounded-lg bg-purple-50">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900">
                    {t("learning_objectives") || "Learning Objectives"}
                  </h4>
                  <ul className="space-y-2">
                    {objectives.map((objective: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-700"
                      >
                        <span className="text-purple-600 font-bold mt-0.5">•</span>
                        <span className="flex-1">{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Author */}
            {authorName && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                <User className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t("label_created_by") || "Created by"}
                  </p>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {authorName}
                  </p>
                </div>
              </div>
            )}

            {/* Created Date */}
            {createdDate && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                <Calendar className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t("created_on") || "Created on"}
                  </p>
                  <p className="text-sm text-gray-900">
                    {new Date(createdDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Last Updated */}
            {lastUpdated && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t("last_updated") || "Last updated"}
                  </p>
                  <p className="text-sm text-gray-900">
                    {new Date(lastUpdated).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 sm:col-span-2">
                <Tag className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-2 text-xs font-medium text-gray-600">
                    {t("tags") || "Tags"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="p-4 border border-green-200 rounded-lg bg-green-50">
            <p className="text-sm leading-relaxed text-green-800">
              <span className="font-semibold">
                {t("ready_to_start") || "Ready to get started?"}
              </span>{" "}
              {t("activity_ready_message") ||
                "This activity has been shared with you. Click 'Start Activity' to begin your journey."}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityMetadataModal;
